import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { usePatchApiWorkspacesWorkspaceId, useGetApiWorkspaces } from '@/api/generated/workspaces/workspaces';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const renameWorkspaceSchema = z.object({
  name: z.string().min(2, 'O nome deve ter no mínimo 2 caracteres'),
});

type RenameWorkspaceFormValues = z.infer<typeof renameWorkspaceSchema>;

interface RenameWorkspaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  currentName: string;
}

export const RenameWorkspaceModal: React.FC<RenameWorkspaceModalProps> = ({
  open,
  onOpenChange,
  workspaceId,
  currentName,
}) => {
  const renameMutation = usePatchApiWorkspacesWorkspaceId();
  const { setWorkspaces } = useWorkspaceStore();
  const { refetch } = useGetApiWorkspaces();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RenameWorkspaceFormValues>({
    resolver: zodResolver(renameWorkspaceSchema),
    defaultValues: {
      name: currentName,
    },
  });

  React.useEffect(() => {
    if (open) {
      reset({ name: currentName });
    }
  }, [open, currentName, reset]);

  const onSubmit = async (data: RenameWorkspaceFormValues) => {
    try {
      await renameMutation.mutateAsync({
        workspaceId,
        data: { name: data.name },
      });
      toast.success('Workspace renomeado com sucesso!');

      const { data: workspacesList } = await refetch();
      if (workspacesList) {
        setWorkspaces(workspacesList as any[]);
      }

      onOpenChange(false);
    } catch {
      // Handled by global interceptor
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Renomear Workspace</DialogTitle>
          <DialogDescription>
            Altere o nome visível deste workspace para você e sua equipe.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="rename-name">Nome do Workspace</Label>
            <Input
              id="rename-name"
              placeholder="Ex: Minha Empresa"
              {...register('name')}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
